import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import { useWorkspace } from '../../context/WorkspaceContext';
import { formatBillingRenewalDate } from '../../lib/billingDisplay';

/**
 * Settings → Billing & plan — workspace subscription summary from Stripe webhooks.
 */
export default function BillingManager() {
  const { workspace, loading } = useWorkspace();
  const billing =
    workspace?.billing && typeof workspace.billing === 'object'
      ? workspace.billing
      : null;

  const planId = useMemo(() => {
    const fromBilling =
      typeof billing?.planId === 'string' && billing.planId.trim()
        ? billing.planId.trim()
        : '';
    if (fromBilling) return fromBilling;
    const legacy =
      workspace?.subscriptionPlan ||
      workspace?.planName ||
      workspace?.plan ||
      '';
    return typeof legacy === 'string' && legacy.trim() ? legacy.trim() : '—';
  }, [billing?.planId, workspace?.subscriptionPlan, workspace?.planName, workspace?.plan]);

  const renewalDate = useMemo(
    () => formatBillingRenewalDate(billing?.currentPeriodEnd),
    [billing?.currentPeriodEnd],
  );

  const billingStatus =
    typeof billing?.status === 'string' && billing.status.trim()
      ? billing.status.trim()
      : 'inactive';

  const statusChipColor =
    billingStatus === 'active' || billingStatus === 'trialing'
      ? 'success'
      : billingStatus === 'past_due'
        ? 'warning'
        : 'default';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Billing & plan
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 560 }}>
        View your workspace subscription status and renewal date. Plan changes from Stripe
        checkout are applied automatically after payment.
      </Typography>

      {billing ? (
        <Paper variant="outlined" sx={{ p: 2.5, maxWidth: 480 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CreditCardOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Current subscription
            </Typography>
            <Chip
              size="small"
              label={billingStatus}
              color={statusChipColor}
              variant="outlined"
              sx={{ ml: 'auto', textTransform: 'capitalize' }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 700 }}>
              {planId}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Renews on {renewalDate}
            </Typography>
          </Box>
        </Paper>
      ) : (
        <Alert severity="info" icon={<CreditCardOutlinedIcon />} sx={{ maxWidth: 560 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            No active billing record
          </Typography>
          <Typography variant="body2">
            Contact your platform administrator to set up or change your workspace plan.
          </Typography>
        </Alert>
      )}
    </Box>
  );
}
